import * as React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DriverTaskKpiRow, SortDirection } from '@klareospace/pharos-lib-types';
import type { DataTableColumn } from '@klareospace/pharos-lib-web-utils';
import { Button, DataTable, ErrorBanner, useDataTableSorting } from '@klareospace/pharos-lib-web-utils';

import type { DriverKpiSortColumn } from './driver-kpis-month.utils';
import { sortDriverKpiRows } from './driver-kpis-month.utils';

// ─── Constants ──────────────────────────────────────────────────────────────

// Mirrors DriverKpisToday's progressive-disclosure cap (same values, kept local since that
// sibling's constants aren't exported).
const INITIAL_VISIBLE_ROWS = 5;
const SHOW_MORE_STEP = 5;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DriverKpisMonthProps {
  isError: boolean;
  isLoading: boolean;
  rows: DriverTaskKpiRow[] | undefined;
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Displays the month-to-date `driver-task-kpis` as a per-driver table — Driver | Total tasks | On time | Late | Failed, sortable client-side, capped with "show more" like `DriverKpisToday`. */
export function DriverKpisMonth({ isError, isLoading, rows }: DriverKpisMonthProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('kpis');
  const [sortBy, setSortBy] = useState<DriverKpiSortColumn | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<SortDirection | undefined>(undefined);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_ROWS);

  function handleSort(field: DriverKpiSortColumn, direction: SortDirection | null): void {
    setSortBy(direction ? field : undefined);
    setSortDirection(direction ?? undefined);
  }

  const sorting = useDataTableSorting<DriverKpiSortColumn>({ onSort: handleSort, sortBy, sortDirection });
  const isTableLoading = isLoading || !rows;
  const sortedRows = useMemo(() => sortDriverKpiRows(rows ?? [], sortBy, sortDirection), [rows, sortBy, sortDirection]);
  const visibleRows = sortedRows.slice(0, visibleCount);
  const hasMore = visibleCount < sortedRows.length;

  const columns = useMemo<DataTableColumn<DriverTaskKpiRow>[]>(
    () => [
      {
        cell: (row): React.ReactNode => row.driverName,
        header: tCommon('fields.driverColumnLabel'),
        key: 'driver',
        sortable: true,
      },
      {
        cell: (row): React.ReactNode => row.assigned,
        header: t('driverTable.columns.totalTasks'),
        key: 'assigned',
        sortable: true,
      },
      {
        cell: (row): React.ReactNode => <span className="font-medium text-success">{row.ontime}</span>,
        header: t('driverTable.columns.ontime'),
        key: 'ontime',
        sortable: true,
      },
      {
        cell: (row): React.ReactNode => row.late,
        header: t('driverTable.columns.late'),
        key: 'late',
        sortable: true,
      },
      {
        cell: (row): React.ReactNode => row.failed,
        header: t('common.failed'),
        key: 'failed',
        sortable: true,
      },
    ],
    [t],
  );

  if (isError) return <ErrorBanner message={t('loadError')} />;

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold uppercase text-muted-foreground">{t('driverTable.title')}</h2>
      </div>
      <DataTable<DriverTaskKpiRow>
        ariaLabel={t('driverTable.month.ariaLabel')}
        columns={columns}
        emptyMessage={t('driverTable.month.empty')}
        isLoading={isTableLoading}
        rowKey={(row): string => row.driverId}
        rows={visibleRows}
        sorting={sorting}
        tableClassName="[&_tr:last-child]:border-b-0"
      />
      {hasMore && (
        <div className="border-t border-border px-3 py-2">
          <Button
            className="text-primary"
            onClick={(): void => setVisibleCount((count) => count + SHOW_MORE_STEP)}
            size="sm"
            type="button"
            variant="ghost"
          >
            {t('driverTable.showMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
